import {
  formatDateToLocaleString,
  isCurrentMonth,
  isPreviousMonth,
  toInclusivePeriodEnd,
  utcIsoToLocalCalendarDate,
} from '@/shared/utils/date-utils';
import { formatDataUsage } from '@/shared/utils/format-utils';
import {
  DataUsage,
  ExhaustionInfo,
  ServiceLine,
  OverageData,
  OverageInfo,
  OverageStatus,
} from '@/shared/types/serviceLineTypes';

/**
 * Get service line overage information
 * 
 * @param overage - Service line overage
 * @returns Service line overage information
 */
export const getOverageInfo = (overage?: OverageData): OverageInfo => {
  const overageStatus = getOverageStatus(overage);
  const value = overage?.mode === 'LIMITED' && overage?.dataAmount ? overage.dataAmount : 0;
  const endDate = toInclusivePeriodEnd(overage?.overageEndDate);
  const endDateForDisplay = endDate ? utcIsoToLocalCalendarDate(endDate) : null;
  const endDateString = endDate ? endDateForDisplay?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : formatDateToLocaleString(endDate);
  switch (overageStatus) {
    case OverageStatus.ACTIVE:
      return {
        status: OverageStatus.ACTIVE,
        dataAmount: value,
        info: `${overage?.mode === 'LIMITED' ? `${overage?.dataAmount} GB -` : 'Unlimited - '} valid until ${endDateString}`,
        shortInfo: overage?.mode === 'LIMITED' ? formatDataUsage(value) : 'Unlimited',
      };
    case OverageStatus.EXPIRED:
      return {
        status: OverageStatus.EXPIRED,
        dataAmount: 0,
        info: `Overage Expired on ${endDateString}`,
        shortInfo: 'Expired',
      };
    case OverageStatus.NO_OVERAGE:
      return {
        status: OverageStatus.NO_OVERAGE,
        dataAmount: 0,
        info: 'No Overage',
        shortInfo: '-',
      };
  }
};

/**
 * Get service line overage status
 * 
 * @param overage - Service line overage
 * @returns Service line overage status
 */
const getOverageStatus = (overage?: OverageData): OverageStatus => {
  if (overage?.overageEndDate) {
    const originalEndDate = new Date(overage.overageEndDate);
    return originalEndDate >= new Date() ? OverageStatus.ACTIVE : OverageStatus.EXPIRED;
  }

  return OverageStatus.NO_OVERAGE;
};

/**
 * Calculates the estimated exhaustion date and provides detailed exhaustion information for a service line
 * 
 * @param serviceLine - Service line
 * @param dataUsages - Data usages
 * @returns Exhaustion information
 */
export const getExhaustionInfoForServiceLine = (serviceLine: ServiceLine): ExhaustionInfo => {
  // Check if we have the necessary data to calculate exhaustion
  if (!serviceLine?.product?.dataProduct?.dataAmount || !serviceLine.dataUsages || serviceLine.dataUsages.length === 0) {
    return {
      exhaustionDate: null,
      daysRemaining: null,
      status: 'safe',
      message: 'Unable to calculate exhaustion date',
      planAdequacy: 'unknown',
      monthlyProjection: null,
      remainingDaysInMonth: null
    };
  }

  // Calculate total data limit (base + overage if allowed)
  const totalDataLimitGB = getTotalDataPlanLimit(serviceLine);

  // Get current billing cycle data usage
  const currentUsageGB = getCurrentMonthDataUsage(serviceLine.dataUsages); // Most recent billing cycle
  const totalDataUsageGB = currentUsageGB?.totalDataUsageGB || 0;
  const remainingDataGB = totalDataLimitGB < Number.POSITIVE_INFINITY ? totalDataLimitGB - totalDataUsageGB : Number.POSITIVE_INFINITY;

  // Calculate remaining days in the current month
  const now = new Date();
  const currentMonth = now.getUTCMonth();
  const currentYear = now.getUTCFullYear();
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const remainingDaysInMonth = daysInMonth - now.getDate();

  // Calculate average daily usage based on total usage and days since billing cycle start
  // Use fractional days (including time of day) for more accurate forecasts
  const billingStartDate = new Date(currentYear, currentMonth, 1, 0, 0, 0, 0); // Start of month at midnight
  const millisecondsSinceStart = now.getTime() - billingStartDate.getTime();
  const daysSinceStart = millisecondsSinceStart / (1000 * 60 * 60 * 24); // Fractional days (e.g., 4.5 for 4 days 12 hours)

  // Ensure minimum of 0.1 days to avoid division by zero and handle edge cases
  const effectiveDaysSinceStart = Math.max(daysSinceStart, 0.1);

  // Calculate monthly projection even for exceeded limits
  let monthlyProjection: number | null = null;
  if (totalDataUsageGB > 0 && effectiveDaysSinceStart > 0) {
    const averageDailyUsageGB = totalDataUsageGB / effectiveDaysSinceStart;
    monthlyProjection = averageDailyUsageGB * daysInMonth;
  }

  // If already exceeded limit or insufficient data, return appropriate response
  if (remainingDataGB <= 0) {
    return {
      exhaustionDate: new Date(),
      daysRemaining: 0,
      status: 'exceeded',
      message: 'Data limit exceeded',
      planAdequacy: 'insufficient',
      monthlyProjection,
      remainingDaysInMonth
    };
  }

  // Ensure we have at least some time elapsed to avoid division by zero
  if (effectiveDaysSinceStart <= 0) {
    return {
      exhaustionDate: null,
      daysRemaining: null,
      status: 'safe',
      message: 'Unable to calculate exhaustion date',
      planAdequacy: 'unknown',
      monthlyProjection: null,
      remainingDaysInMonth
    };
  }

  const averageDailyUsageGB = totalDataUsageGB / effectiveDaysSinceStart;

  // If no usage pattern, return null
  if (averageDailyUsageGB <= 0) {
    return {
      exhaustionDate: null,
      daysRemaining: null,
      status: 'safe',
      message: 'Unable to calculate exhaustion date',
      planAdequacy: 'unknown',
      monthlyProjection,
      remainingDaysInMonth
    };
  }

  // Calculate days until exhaustion (fractional for accuracy)
  const daysUntilExhaustion = remainingDataGB / averageDailyUsageGB;

  // Calculate exhaustion date using fractional days for more accurate forecast
  const exhaustionDate = new Date();
  const millisecondsUntilExhaustion = daysUntilExhaustion * (1000 * 60 * 60 * 24);
  exhaustionDate.setTime(now.getTime() + millisecondsUntilExhaustion);

  // Limit exhaustion date to end of current month
  const endOfMonth = new Date(currentYear, currentMonth + 1, 0, 23, 59, 59, 999); // End of day

  if (exhaustionDate > endOfMonth) {
    exhaustionDate.setTime(endOfMonth.getTime());
  }

  // Additional safety check - ensure we never return a date beyond current month
  const finalMonth = exhaustionDate.getUTCMonth();
  const finalYear = exhaustionDate.getUTCFullYear();
  if (finalMonth !== currentMonth || finalYear !== currentYear) {
    exhaustionDate.setTime(endOfMonth.getTime());
  }

  // Calculate days remaining using fractional days for accuracy
  const daysRemaining = (exhaustionDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);

  // Determine status based on days remaining
  let status: ExhaustionInfo['status'];
  let message: string;

  // Format days remaining for display
  // For critical/warning cases, show 1 decimal place for accuracy
  // For safe cases, round to whole days
  const formatDaysRemaining = (days: number, showDecimal: boolean = false): string => {
    if (showDecimal) {
      const rounded = Math.round(days * 10) / 10;
      return rounded.toFixed(1);
    }
    return Math.round(days).toString();
  };

  if (daysRemaining <= 0) {
    status = 'exceeded';
    message = 'Data limit exceeded';
  } else if (daysRemaining <= 3) {
    status = 'critical';
    const formatted = formatDaysRemaining(daysRemaining, true);
    const daysValue = Number.parseFloat(formatted);
    const isPlural = daysValue > 1;
    message = `Critical: ${formatted} day${isPlural ? 's' : ''} remaining`;
  } else if (daysRemaining <= 7) {
    status = 'warning';
    message = `Warning: ${formatDaysRemaining(daysRemaining, true)} days remaining`;
  } else {
    status = 'safe';
    message = `${formatDaysRemaining(daysRemaining)} days remaining`;
  }

  // Assess plan adequacy for the remaining month
  let planAdequacy: ExhaustionInfo['planAdequacy'] = 'unknown';

  if (monthlyProjection && monthlyProjection > 0) {
    if (monthlyProjection <= totalDataLimitGB) {
      planAdequacy = 'sufficient';
    } else {
      planAdequacy = 'insufficient';
    }
  } else {
    planAdequacy = 'unknown';
  }

  return {
    exhaustionDate,
    daysRemaining,
    status,
    message,
    planAdequacy,
    monthlyProjection,
    remainingDaysInMonth
  };
};

/**
 * Get total data usage for a service line in the previous month
 */
export const getPreviousMonthDataUsage = (dataUsages?: DataUsage[]): DataUsage | null => {
  if (!dataUsages || dataUsages.length === 0) return null;
  return dataUsages.find((usage) => isPreviousMonth(usage.startDate)) ?? null;
};

/**
 * Get total data usage for a service line in the current month
 */
export const getCurrentMonthDataUsage = (dataUsages?: DataUsage[]): DataUsage | null => {
  if (!dataUsages || dataUsages.length === 0) return null;
  return dataUsages.find((usage) => isCurrentMonth(usage.startDate)) ?? null;
};

/**
 * Get usage percentage for a service line
 */
export const getCurrentMonthUsagePercentage = (serviceLine: ServiceLine): number | null => {
  // if (hasActiveUnlimitedOverage(serviceLine.overage)) return null;
  if (!serviceLine.dataUsages || serviceLine.dataUsages.length === 0) return 0;
  const currentMonthUsage = getCurrentMonthDataUsage(serviceLine.dataUsages);
  const totalDataUsageGB = currentMonthUsage?.totalDataUsageGB || 0;
  const totalDataPlan = getTotalDataPlanLimit(serviceLine);
  return totalDataPlan > 0 && totalDataPlan < Number.POSITIVE_INFINITY ? (totalDataUsageGB / totalDataPlan) * 100 : null;
};

/**
 * Get total plan limit for a service line (base + overage if allowed).
 */
const getTotalDataPlanLimit = (serviceLine: ServiceLine): number => {
  const dataPlan = serviceLine.product?.dataProduct?.dataAmount || 0;
  const overageInfo = getOverageInfo(serviceLine.overage);
  const overageAmount = overageInfo.status === OverageStatus.ACTIVE ? (serviceLine.overage.mode === 'LIMITED' ? overageInfo.dataAmount : Number.POSITIVE_INFINITY) : 0;
  return dataPlan + overageAmount;
};